import { roDate, roleLabel, ROLES, type Site, type Team, type TeamMember, t } from '@procumi/shared';
import { useEffect, useRef, useState } from 'react';
import { Avatar } from './Avatar';
import { ErrorBox, Loading } from './StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { ask } from '../lib/confirm';

/**
 * Cine mai lucrează în firmă: fila „Echipa” din „Contul firmei”.
 *
 * Fiecare om are un rol (Achiziții, Aprobator sau Șantier, vezi
 * shared/roles.ts) și poate fi administrator, în afară de cel de șantier.
 * Oamenii de pe șantier nu ocupă loc în pachet. Doar administratorii invită, scot și schimbă roluri;
 * ceilalți văd echipa, fără butoane.
 *
 * Invitația pleacă pe mail din aplicație; linkul se arată și aici, ca rezervă (WhatsApp, Spam),
 * **o singură dată**: în bază stă doar amprenta codului, ca la sesiuni.
 */
export function TeamSection({ toast, isAdmin }: { toast: Toast; isAdmin: boolean }) {
  const { data, error, loading, reload } = useAsync(() => api.team(), [], 'team');
  const sites = useAsync(() => api.sites(), [], 'sites');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('buyer');
  const [admin, setAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Ultimul link făcut, ținut până pleci de pe ecran. */
  const [link, setLink] = useState<{ email: string; url: string; mailed: boolean } | null>(null);

  const invita = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const facut = await api.invite({
        email: email.trim(),
        role,
        isAdmin: admin && role !== 'site',
      });
      setLink({ email: facut.invite.email, url: facut.link, mailed: facut.mailed });
      setEmail('');
      setAdmin(false);
      reload();
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const retrage = async (id: string, adresa: string) => {
    const ok = await ask({
      title: t('Retragi invitația?'),
      message: t('Linkul trimis la {0} nu va mai merge.', adresa),
      confirm: t('Retrage invitația'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.revokeInvite(id);
      toast(t('Invitație retrasă'));
      reload();
    } catch (err) {
      toast(errMsg(err));
    }
  };

  const scoate = async (id: string, nume: string) => {
    const ok = await ask({
      title: t('Scoți pe {0} din firmă?', nume),
      message: t(
        'Nu mai poate intra și iese din aplicație pe loc. Numele rămâne pe cererile făcute până acum.',
      ),
      confirm: t('Scoate din firmă'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deactivateUser(id);
      toast(t('Scos din firmă'));
      reload();
    } catch (err) {
      toast(errMsg(err));
    }
  };

  const readu = async (id: string) => {
    try {
      await api.reactivateUser(id);
      toast(t('Readus în firmă'));
      reload();
    } catch (err) {
      toast(errMsg(err));
    }
  };

  const schimba = async (u: TeamMember, body: { role?: string; isAdmin?: boolean }) => {
    try {
      await api.updateMember(u.id, body);
      toast(
        body.role !== undefined
          ? t('{0} e acum {1}', u.name, t(roleLabel(body.role)))
          : body.isAdmin
            ? t('{0} e acum administrator', u.name)
            : t('{0} nu mai e administrator', u.name),
      );
      reload();
    } catch (err) {
      toast(errMsg(err));
    }
  };

  const team: Team = data ?? { users: [], invites: [] };
  const activi = team.users.filter((u) => !u.deactivatedAt);
  const scosi = team.users.filter((u) => u.deactivatedAt);

  return (
    <>
      {loading ? <Loading /> : null}
      {!loading && error ? <ErrorBox error={error} onRetry={reload} /> : null}

      {!loading && !error ? (
        <>
          {isAdmin ? null : (
            <p className="read-warn" style={{ marginTop: 0 }}>
              {t('Doar un administrator al contului invită colegi și le schimbă rolul.')}
            </p>
          )}
          {isAdmin ? (
            <section className="sheet" style={{ marginBottom: 'var(--space-6)' }}>
              <div className="pad" style={{ paddingBottom: 'var(--space-2)' }}>
                <div className="section-title">
                  <h2>{t('Invită un coleg')}</h2>
                  <span className="muted" style={{ fontSize: 14 }}>
                    {t('linkul e valabil 14 zile')}
                  </span>
                </div>
                <form className="list-tools" style={{ marginBottom: 0 }} onSubmit={invita}>
                  <input
                    className="inp"
                    type="email"
                    value={email}
                    placeholder={t('email.colegului@firma.ro')}
                    aria-label={t('Emailul colegului')}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <select
                    className="inp team-role"
                    value={role}
                    aria-label={t('Rolul colegului')}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    {ROLES.map((r) => (
                      <option key={r.id} value={r.id}>
                        {t(r.label)}
                      </option>
                    ))}
                  </select>
                  <label
                    className="team-admin"
                    title={role === 'site' ? t('Contul de șantier nu poate fi administrator') : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={admin && role !== 'site'}
                      disabled={role === 'site'}
                      onChange={(e) => setAdmin(e.target.checked)}
                    />
                    {t('administrator')}
                  </label>
                  <button className="btn primary" type="submit" disabled={busy || !email.trim()}>
                    {busy ? t('Se face…') : t('Fă invitația')}
                  </button>
                </form>
                <p className="hint" style={{ margin: 'var(--space-2) 0 0' }}>
                  {t(ROLES.find((r) => r.id === role)?.label ?? '')}:{' '}
                  {t(ROLES.find((r) => r.id === role)?.hint ?? '')}.
                  {role === 'site' ? '' : t(' Administratorul schimbă și datele firmei, echipa și pachetul.')}
                </p>

                {link ? (
                  <div className="invite-link">
                    <div>
                      <b>
                        {link.mailed
                          ? t('Invitația a plecat pe mail la {0}', link.email)
                          : t('Mailul către {0} n-a plecat', link.email)}
                      </b>
                      <div className="who">
                        {t(
                          '{0} Linkul nu se mai arată a doua oară: dacă îl pierzi, faci altă invitație.',
                          link.mailed
                            ? t(
                                'Dacă nu-l găsește (uneori ajunge în Spam), trimite-i linkul de mai jos pe WhatsApp.',
                              )
                            : t('Trimite-i tu linkul, pe mail sau pe WhatsApp.'),
                        )}
                      </div>
                      <code>{link.url}</code>
                    </div>
                    <button className="btn small" onClick={() => void copyText(link.url, toast)}>
                      {t('Copiază')}
                    </button>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {team.invites.length ? (
            <section className="sheet" style={{ marginBottom: 'var(--space-6)' }}>
              <div className="pad" style={{ paddingBottom: 'var(--space-2)' }}>
                <div className="section-title">
                  <h2>{t('Invitații trimise')}</h2>
                  <span className="muted" style={{ fontSize: 14 }}>
                    {team.invites.length}
                  </span>
                </div>
              </div>
              <div className="sup-list">
                {team.invites.map((inv) => (
                  <div className="sup" key={inv.id}>
                    <div>
                      <div className="name">{inv.email}</div>
                      <div className="who">
                        {t(
                          '{0}{1} · {2}expiră {3}',
                          t(roleLabel(inv.role)),
                          inv.isAdmin ? t(' · administrator') : '',
                          inv.invitedBy ? t('invitat de {0} · ', inv.invitedBy) : '',
                          roDate(inv.expiresAt.slice(0, 10)),
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="pill wait">{t('n-a intrat încă')}</span>
                    </div>
                    <div className="acts">
                      {isAdmin ? (
                        <button className="btn small" onClick={() => void retrage(inv.id, inv.email)}>
                          {t('Retrage')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="sheet panel team-panel">
            <header className="panel-h">
              <h2>{t('În firmă')}</h2>
              <span className="panel-hint">
                {activi.length} {activi.length === 1 ? t('persoană') : t('persoane')}
              </span>
              <span className="panel-act team-mix">
                {ROLES.map((r) => {
                  const n = activi.filter((u) => u.role === r.id).length;
                  return n ? (
                    <span key={r.id}>
                      <b>{n}</b> {t(r.label).toLowerCase()}
                    </span>
                  ) : null;
                })}
              </span>
            </header>
            <div className="team-head" aria-hidden="true">
              <span>{t('Om')}</span>
              <span>{t('Rol')}</span>
              <span>{t('Răspunde de șantierele')}</span>
              <span />
            </div>
            <ul className="team-list">
              {activi.map((u) => (
                <li className="team-row" key={u.id}>
                  <div className="team-who">
                    <Avatar name={u.name} size="lg" userId={u.id} avatarAt={u.avatarAt} />
                    <div>
                      <b>
                        {u.name}
                        {u.isYou ? <span className="team-you">{t('tu')}</span> : null}
                      </b>
                      <span>{u.email}</span>
                    </div>
                  </div>
                  <div className="team-roles">
                    {isAdmin ? (
                      <>
                        <select
                          className="inp team-role"
                          value={u.role}
                          aria-label={t('Rolul lui {0}', u.name)}
                          onChange={(e) => void schimba(u, { role: e.target.value })}
                        >
                          {ROLES.map((r) => (
                            <option key={r.id} value={r.id}>
                              {t(r.label)}
                            </option>
                          ))}
                        </select>
                        <label className="team-admin">
                          <input
                            type="checkbox"
                            checked={u.isAdmin}
                            disabled={u.role === 'site'}
                            onChange={(e) => void schimba(u, { isAdmin: e.target.checked })}
                          />
                          {t('admin')}
                        </label>
                      </>
                    ) : (
                      <>
                        <span className={`pill role-${u.role}`}>{t(roleLabel(u.role))}</span>
                        {u.isAdmin ? <span className="pill off">{t('admin')}</span> : null}
                      </>
                    )}
                  </div>
                  <div className="team-sites">
                    {u.role === 'site' ? (
                      <span className="muted">{t('trimite necesarul de pe șantier')}</span>
                    ) : (
                      <SitesPicker
                        member={u}
                        sites={sites.data ?? []}
                        editable={isAdmin}
                        toast={toast}
                        onSaved={reload}
                      />
                    )}
                  </div>
                  <div className="acts">
                    {u.isYou || !isAdmin ? null : (
                      <button className="btn small" onClick={() => void scoate(u.id, u.name)}>
                        {t('Scoate')}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {scosi.length ? (
            <section className="sheet" style={{ marginTop: 'var(--space-6)' }}>
              <div className="pad" style={{ paddingBottom: 'var(--space-2)' }}>
                <div className="section-title">
                  <h2>{t('Scoși din firmă')}</h2>
                  <span className="muted" style={{ fontSize: 14 }}>
                    {t('rămân scriși pe ce au făcut')}
                  </span>
                </div>
              </div>
              <div className="sup-list">
                {scosi.map((u) => (
                  <div className="sup" key={u.id}>
                    <div>
                      <div className="name">{u.name}</div>
                      <div className="who">{u.email}</div>
                    </div>
                    <div>
                      <span className="pill off">{t('fără acces')}</span>
                    </div>
                    <div className="acts">
                      {isAdmin ? (
                        <button className="btn small" onClick={() => void readu(u.id)}>
                          {t('Readu în firmă')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/**
 * Șantierele de care răspunde un coleg: etichete cu numele lor și, pentru
 * administrator, o listă cu bife. Responsabilii primesc mailurile despre
 * comentariile de pe cererile șantierelor lor.
 */
function SitesPicker({
  member,
  sites,
  editable,
  toast,
  onSaved,
}: {
  member: TeamMember;
  sites: Site[];
  editable: boolean;
  toast: Toast;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const mine = sites.filter((s) => member.siteIds.includes(s.id));

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const toggle = async (id: string, on: boolean) => {
    const next = on ? [...member.siteIds, id] : member.siteIds.filter((x) => x !== id);
    setBusy(true);
    try {
      await api.setMemberSites(member.id, next);
      onSaved();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sites-pick" ref={box}>
      {mine.length ? (
        <span className="site-chips">
          {mine.slice(0, 3).map((s) => (
            <span className="site-chip" key={s.id}>
              {s.name}
            </span>
          ))}
          {mine.length > 3 ? <span className="muted">+{mine.length - 3}</span> : null}
        </span>
      ) : (
        <span className="muted">{t('niciunul')}</span>
      )}
      {editable && sites.length ? (
        <button type="button" className="link sites-edit" aria-expanded={open} onClick={() => setOpen(!open)}>
          {mine.length ? t('Schimbă') : t('Alege')}
        </button>
      ) : null}
      {open ? (
        <div className="sites-pop" role="dialog" aria-label={t('Șantierele lui {0}', member.name)}>
          <p>{t('Primește mailurile despre comentariile de pe cererile acestor șantiere.')}</p>
          {sites.map((s) => (
            <label key={s.id}>
              <input
                type="checkbox"
                disabled={busy}
                checked={member.siteIds.includes(s.id)}
                onChange={(e) => void toggle(s.id, e.target.checked)}
              />
              <span>{s.name}</span>
              {s.locality ? <small>{s.locality}</small> : null}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
